import { UserRole } from "../types/UserRole";
import { normalizeStatus } from "./discussionStatusHelpers";

/**
 * Represents an action that can be taken when submitting a comment
 */
export interface CommentAction {
  key: string;
  label: string;
  status?: string; // If defined, this action will change the discussion status
  requiresComment?: boolean; // If true, comment text is required (cannot be empty)
}

/**
 * Get the terminal (final) status for a discussion type
 * - ReviewPoints: "Resolved"
 * - Everything else: "Closed"
 */
export function getTerminalStatus(discussionType: string): string {
  return discussionType === "review_point" ? "Resolved" : "Closed";
}

/**
 * Get available comment actions based on user role, discussion type, and current status
 */
export function getCommentActions(
  userRole: UserRole,
  discussionType: string,
  currentStatus?: string
): { primaryAction: CommentAction; menuActions: CommentAction[] } {
  const isReviewPoint = discussionType === "review_point";
  const normalizedCurrentStatus = normalizeStatus(currentStatus);

  // Helper function to filter out actions that match the current status
  const filterCurrentStatus = (actions: CommentAction[]): CommentAction[] => {
    return actions.filter(
      (action) => !action.status || normalizeStatus(action.status) !== normalizedCurrentStatus
    );
  };

  // Helper to build final result, ensuring primary action doesn't match current status
  const buildResult = (
    primary: CommentAction,
    menu: CommentAction[]
  ): { primaryAction: CommentAction; menuActions: CommentAction[] } => {
    const filteredMenu = filterCurrentStatus([...menu]);

    // If primary action matches current status, pick first non-matching action from menu
    if (primary.status && normalizeStatus(primary.status) === normalizedCurrentStatus) {
      const statusActions = filteredMenu.filter((a) => a.status);
      const newPrimary = statusActions.length > 0 ? statusActions[0] : commentOnly;
      return {
        primaryAction: newPrimary,
        menuActions: filteredMenu.filter((a) => a.key !== newPrimary.key),
      };
    }

    return { primaryAction: primary, menuActions: filteredMenu };
  };

  // "Comment only" is always available in the menu (no status change)
  const commentOnly: CommentAction = {
    key: "comment-only",
    label: "Comment Only",
    requiresComment: true,
  };

  // Review Point discussions have restricted status transitions
  if (isReviewPoint) {
    // Users can only comment on Review Points, no status changes
    if (userRole === UserRole.User) {
      const primaryAction: CommentAction = {
        key: "comment-only",
        label: "Send",
        requiresComment: true,
      };

      return { primaryAction, menuActions: [] };
    }

    // Preparers can set to Open or Processed
    if (userRole === UserRole.Preparer) {
      const primaryAction: CommentAction = {
        key: "mark-processed",
        label: "Mark Processed",
        status: "Processed",
      };

      // Ordered by workflow progression: Open → Processed
      const menuActions: CommentAction[] = [
        {
          key: "mark-open",
          label: "Mark Open",
          status: "Open",
        },
        commentOnly,
      ];

      return buildResult(primaryAction, menuActions);
    }

    // Reviewers and Partners can set any Review Point status
    if (
      userRole === UserRole.Reviewer ||
      userRole === UserRole.Partner
    ) {
      const markOpen: CommentAction = { key: "mark-open", label: "Mark Open", status: "Open" };
      const markProcessed: CommentAction = { key: "mark-processed", label: "Mark Processed", status: "Processed" };
      const markResolved: CommentAction = { key: "mark-resolved", label: "Mark Resolved", status: "Resolved" };
      const reworkRequired: CommentAction = { key: "rework-required", label: "Rework Required", status: "Rework Required" };

      // For Reviewers only, when the point is still Open (or brand new),
      // the default action is "Mark Processed" — the next step in the
      // workflow — rather than "Mark Resolved". Prevents Reviewers from
      // accidentally skipping the Processed → Resolved review step by
      // clicking the primary action without reading it. Partners see
      // "Mark Resolved" as the default regardless of status — the Partner
      // action on a review point is virtually always the final resolve, so
      // biasing them towards "Processed" would just make them click twice.
      // Reviewers can still Mark Resolved directly via the overflow menu.
      const isOpen = !currentStatus || normalizedCurrentStatus === normalizeStatus("Open");
      const defaultToProcessed = isOpen && userRole === UserRole.Reviewer;

      const primaryAction: CommentAction = defaultToProcessed ? markProcessed : markResolved;

      // Menu is ordered by workflow progression: Open → Processed → Rework
      // Required → Resolved. Whichever of markProcessed / markResolved is
      // NOT the primary lives in the menu at its natural position.
      const menuActions: CommentAction[] = defaultToProcessed
        ? [markOpen, markResolved, reworkRequired, commentOnly]
        : [markOpen, markProcessed, reworkRequired, commentOnly];

      return buildResult(primaryAction, menuActions);
    }
  }

  // Non-Review Point discussions: everyone can only set to Open or Closed
  const primaryAction: CommentAction = {
    key: "close",
    label: "Close",
    status: "Closed",
  };

  // Ordered by workflow progression: Open → Closed
  const menuActions: CommentAction[] = [
    {
      key: "mark-open",
      label: "Mark Open",
      status: "Open",
    },
    commentOnly,
  ];

  return buildResult(primaryAction, menuActions);
}
